import { useState, forwardRef } from "react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { AuthenticatedImage } from "@/components/ui/AuthenticatedImage";
import { useAvatarProxyUrl } from "@/hooks/useImageProxyUrl";
import { getProfileInitials, DisplayableProfile } from "@/lib/profileUtils";
import { cn } from "@/lib/utils";

interface ProfileAvatarProps {
  profile: DisplayableProfile | null | undefined;
  size?: "xs" | "sm" | "md" | "lg";
  className?: string;
}

const sizeClasses = {
  xs: "h-5 w-5",
  sm: "h-6 w-6",
  md: "h-8 w-8",
  lg: "h-10 w-10",
};

const textSizes = {
  xs: "text-[10px]",
  sm: "text-xs",
  md: "text-sm",
  lg: "text-sm",
};

export const ProfileAvatar = forwardRef<HTMLDivElement, ProfileAvatarProps>(function ProfileAvatar(
  { profile, size = "sm", className },
  ref,
) {
  const avatarProxyUrl = useAvatarProxyUrl(profile?.avatar_url);
  const initials = getProfileInitials(profile);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const showFallback = !avatarProxyUrl || imageError || !imageLoaded;

  return (
    <Avatar ref={ref} className={cn(sizeClasses[size], "shrink-0", className)}>
      {avatarProxyUrl && !imageError && (
        <AuthenticatedImage
          src={avatarProxyUrl}
          alt="Avatar"
          className="h-full w-full object-cover rounded-full"
          showSkeleton={false}
          showErrorUI={false}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageError(true)}
        />
      )}
      {showFallback && (
        <AvatarFallback className={cn(textSizes[size], "bg-muted")}>{initials}</AvatarFallback>
      )}
    </Avatar>
  );
});
